import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getMentions,
  getSentimentStats,
  getSentimentTrends,
  getSportsTicker,
  updateMention,
} from '../api/client.js';
import MentionsTable from '../components/MentionsTable.jsx';
import SentimentLayout from '../components/SentimentLayout.jsx';
import SentimentTrendChart from '../components/SentimentTrendChart.jsx';
import { SportsTickerBar } from '../components/SportsTicker.jsx';
import StatCard from '../components/StatCard.jsx';
import usePolling from '../hooks/usePolling.js';
import {
  PRODUCT_OPTIONS,
  SENTIMENT_OPTIONS,
  SOURCE_FILTERS,
  sentimentScoreColor,
} from '../utils/sentiment.js';

const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const TREND_OPTIONS = [7, 30, 90];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'unreviewed', label: 'Business as Usual' },
  { value: 'glowing', label: 'Glowing' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'client_solution_center', label: 'Client Solution Center' },
  { value: 'resolved', label: 'Resolved' },
];

const EMPTY_FILTERS = {
  search: undefined,
  source_type: undefined,
  sentiment: undefined,
  status: undefined,
  is_migration_related: undefined,
  is_urgent: undefined,
  submitted_to_command_center: undefined,
  product: undefined,
  start_date: undefined,
  end_date: undefined,
};

export default function SentimentDashboardPage() {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [trends, setTrends] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(30);
  // Headline cards track the selected trend window (7/30/90 days).
  const [cardStats, setCardStats] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('published_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);

  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const initialTotal = useRef(null);

  const { data: sportsData, loading: sportsLoading } = usePolling(getSportsTicker, 60000);

  const hasActive = Object.values(filters).some((v) => v !== undefined && v !== '');

  // --- Stats (mount + baseline for polling) ---
  const loadStats = useCallback(async (rebase = false) => {
    const s = await getSentimentStats();
    setStats(s);
    setStatsLoading(false);
    if (rebase || initialTotal.current === null) {
      initialTotal.current = s.total;
      setNewCount(0);
    }
    return s;
  }, []);

  useEffect(() => {
    loadStats().catch(() => setStatsLoading(false));
  }, [loadStats]);

  // Poll for new mentions every 5 minutes (stats only).
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const s = await getSentimentStats();
        if (typeof s?.total === 'number' && initialTotal.current !== null && s.total > initialTotal.current) {
          setNewCount(s.total - initialTotal.current);
        }
      } catch {
        // silent
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // --- Trends + window-scoped headline cards ---
  useEffect(() => {
    let cancelled = false;
    setTrendsLoading(true);
    getSentimentTrends({ days: trendDays })
      .then((t) => !cancelled && setTrends(t))
      .catch(() => {})
      .finally(() => !cancelled && setTrendsLoading(false));
    getSentimentStats({ window_days: trendDays })
      .then((s) => !cancelled && setCardStats(s))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [trendDays]);

  // --- Mentions list ---
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMentions({
      ...filters,
      page,
      page_size: PAGE_SIZE,
      sort_by: sortBy,
      sort_order: sortOrder,
    })
      .then((res) => !cancelled && setData(res))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters, page, sortBy, sortOrder]);

  // Debounced search → filters.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput || undefined }));
      setPage(1);
    }, 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  function updateFilter(patch) {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
    setActiveQuickFilter(null);
  }

  function handleQuickFilter(key, patch) {
    setFilters({ ...EMPTY_FILTERS, ...patch });
    setSearchInput('');
    setPage(1);
    setActiveQuickFilter(key);
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setActiveQuickFilter(null);
    setPage(1);
  }

  async function handleStatusChange(id, next) {
    await updateMention(id, { status: next });
    setData((d) =>
      d
        ? { ...d, results: d.results.map((m) => (m.id === id ? { ...m, status: next } : m)) }
        : d,
    );
    loadStats().catch(() => {});
  }

  async function refreshNow() {
    setBannerDismissed(false);
    await loadStats(true);
    // Re-trigger the list fetch by nudging page (kept same) via filter identity.
    setFilters((f) => ({ ...f }));
  }

  const mentions = data?.results || [];
  const total = data?.total || 0;
  const pages = data?.pages || 0;
  const migrationToday = stats?.migration_related_today ?? 0;

  return (
    <SentimentLayout
      stats={stats}
      statsLoading={statsLoading}
      activeQuickFilter={activeQuickFilter}
      onQuickFilter={handleQuickFilter}
    >
      {newCount > 0 && !bannerDismissed && (
        <button
          type="button"
          className="new-submissions-banner"
          onClick={refreshNow}
          aria-live="polite"
        >
          {newCount} new mention{newCount === 1 ? '' : 's'} — Click to refresh
        </button>
      )}

      {migrationToday > 0 && !bannerDismissed && (
        <div className="migration-banner" role="alert">
          <span>
            ⚠️ {migrationToday} Evolution-related mention{migrationToday === 1 ? '' : 's'} detected in
            the last 24 hours
          </span>
          <button
            type="button"
            className="migration-banner__dismiss"
            onClick={() => setBannerDismissed(true)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <div className="stat-cards-row">
        <StatCard label={`Total Mentions (Last ${trendDays} Days)`} value={cardStats?.total ?? '—'} icon="📊" />
        <StatCard
          label="Avg Sentiment Score"
          value={
            cardStats?.avg_sentiment_score != null ? (
              <span style={{ color: sentimentScoreColor(cardStats.avg_sentiment_score) }}>
                {cardStats.avg_sentiment_score.toFixed(1)} / 5.0
              </span>
            ) : (
              '—'
            )
          }
          icon="🧭"
        />
        <StatCard
          label="Evolution Related"
          value={cardStats?.migration_related ?? '—'}
          icon="🔴"
          accent="critical"
        />
        <StatCard
          label="Urgent"
          value={cardStats?.urgent_count ?? '—'}
          icon="⚡"
          accent={cardStats?.urgent_count > 0 ? 'critical' : 'primary'}
        />
      </div>

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 15 }}>Sentiment Trend — Last {trendDays} Days</h2>
          <div className="trend-toggle">
            {TREND_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`btn btn--secondary${trendDays === d ? ' btn--active' : ''}`}
                onClick={() => setTrendDays(d)}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>
        <div className="card__body">
          <SentimentTrendChart trends={trends} loading={trendsLoading} />
        </div>
      </section>

      <div className="filter-bar" style={{ marginTop: 16 }}>
        <div className="filter-bar__search">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search mention content…"
            aria-label="Search mentions"
          />
        </div>
        <select
          className="filter-bar__select"
          value={filters.source_type || ''}
          onChange={(e) => updateFilter({ source_type: e.target.value || undefined })}
          aria-label="Filter by source"
        >
          {SOURCE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="filter-bar__select"
          value={filters.sentiment || ''}
          onChange={(e) => updateFilter({ sentiment: e.target.value || undefined })}
          aria-label="Filter by sentiment"
        >
          {SENTIMENT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="filter-bar__select"
          value={filters.status || ''}
          onChange={(e) => updateFilter({ status: e.target.value || undefined })}
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="filter-bar__select"
          value={filters.is_migration_related ? 'yes' : ''}
          onChange={(e) => updateFilter({ is_migration_related: e.target.value === 'yes' ? true : undefined })}
          aria-label="Filter by Evolution"
        >
          <option value="">All Mentions</option>
          <option value="yes">Evolution Related Only</option>
        </select>
        <select
          className="filter-bar__select"
          value={filters.is_urgent ? 'yes' : ''}
          onChange={(e) => updateFilter({ is_urgent: e.target.value === 'yes' ? true : undefined })}
          aria-label="Filter by urgent"
        >
          <option value="">All Urgency</option>
          <option value="yes">Urgent Only</option>
        </select>
        <select
          className="filter-bar__select"
          value={filters.product || ''}
          onChange={(e) => updateFilter({ product: e.target.value || undefined })}
          aria-label="Filter by product"
        >
          {PRODUCT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="date"
          className="filter-bar__select"
          value={filters.start_date || ''}
          onChange={(e) => updateFilter({ start_date: e.target.value || undefined })}
          aria-label="Start date"
        />
        <input
          type="date"
          className="filter-bar__select"
          value={filters.end_date || ''}
          onChange={(e) => updateFilter({ end_date: e.target.value || undefined })}
          aria-label="End date"
        />
        {(hasActive || activeQuickFilter) && (
          <button type="button" className="filter-bar__clear" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <MentionsTable
          mentions={mentions}
          loading={loading}
          onStatusChange={handleStatusChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(by, order) => {
            setSortBy(by);
            setSortOrder(order);
          }}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          pages={pages}
          onPageChange={setPage}
          onClearFilters={clearFilters}
        />
      </div>

      <SportsTickerBar data={sportsData} loading={sportsLoading} />
    </SentimentLayout>
  );
}
